import { useEffect, useRef } from "react";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { useAdminSession } from "./useAdminSession";

/**
 * Automatically refreshes admin session ~30s before expiry.
 * Logs each step for debugging and quick testing.
 */
export const useAutoAdminSessionRefresh = () => {
  const { mutate: refreshSession } = useAdminSession();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const refreshToken = localStorage.getItem("refresh_token");
    const expiresIn = Number(localStorage.getItem("expires_in"));

    if (!refreshToken || !expiresIn) {
      return;
    }

    const currentTime = dayjs().unix();
    // 🔥 TEST MODE: refresh 30s before expiry
    const timeUntilRefresh = (expiresIn - currentTime - 30) * 1000;

    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);

    refreshTimerRef.current = setTimeout(() => {
      refreshSession(
        { refresh_token: refreshToken },
        {
          onSuccess: (data: any) => {
            if (data?.accessToken && data?.refresh_token && data?.expires_in) {
              Cookies.set("userToken", data.accessToken, {
                secure: true,
                sameSite: "Strict",
              });

              localStorage.setItem("refresh_token", data.refresh_token);
              localStorage.setItem(
                "expires_in",
                (dayjs().unix() + 30).toString()
              );
            }
          },
          onError: (error) => {
            console.error("[AutoSession] ❌ Token refresh failed:", error);
            Cookies.remove("userToken");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("expires_in");
            window.location.href = "/login";
          },
        }
      );
    }, Math.max(timeUntilRefresh, 0));

    // Cleanup
    return () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [refreshSession]);
};
